import {API} from "./base";
import {TemperatureAPI} from "./temperature";
import {GenericResponse} from "./generic";

export type LogSeverity = "DEBUG" | "INFO" | "WARNING" | "ERROR" | "CRITICAL"

export type LogOpts = {
    since?: string;
    severity?: LogSeverity;
    limit?: number;
};

export type LogEntry = {
    datetime: string;
    severity: LogSeverity;
    message: string;
};

export type LogResponse = {
    log: LogEntry[];
};

export type Token = {
    name: string;
    ipRegEx: string;
    scope: string[];
    until: string | null;
};

export type ExistingToken = {
    id: number
} & Token;

export type ApiKeysResponse<T extends ExistingToken = ExistingToken> = GenericResponse<{
    tokens: T[];
}>;

export type TokenScope = {
    name: string;
}

export type TokenScopesResponse = GenericResponse<{
    scopes: TokenScope[];
}>

export class AdminAPI extends API {
    constructor(api: TemperatureAPI) {
        super(api.clientId, api.refreshTokenUrl, "/admin/");
    }

    async log(opts: LogOpts) {
        const fd = new URLSearchParams();

        if (opts.since !== undefined) {
            fd.set("since", opts.since);
        }

        if (opts.severity !== undefined) {
            fd.set("severity", opts.severity);
        }

        if (opts.limit !== undefined) {
            fd.set("limit", opts.limit.toString());
        }

        const resp = await this.fetchJSON<LogResponse>("?action=log", {
            method: "POST",
            body: fd
        });

        return resp;
    }

    async apiKeys() {
        return await this.fetchJSON<ApiKeysResponse>("/api/v3/token/");
    }

    async createApiKey(token: Token) {
        return await this.postJSON<ApiKeysResponse<ExistingToken & {key: string}>>("/api/v3/token/", [token]);
    }

    async modifyApiKey(token: ExistingToken) {
        return await this.postJSON<ApiKeysResponse>("/api/v3/token/", [token], "PATCH");
    }

    async removeApiKey(tokenId: number): Promise<boolean> {
        try {
            await this.fetchJSON(`/api/v3/token/${tokenId}`, {
                method: "DELETE"
            });
            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    async tokenScopes(): Promise<string[]> {
        return (await this.fetchJSON<TokenScopesResponse>("/api/v3/token/scopes")).scopes.map(scope => scope.name);
    }
}
